/**
 * PlayoffSetup.js - Playoff bracket setup and score entry
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig } from '../utils/sportConfig';
import './PlayoffSetup.css';

function PlayoffSetup({ leagueId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Data from API
  const [playoffs, setPlayoffs] = useState(null);
  const [standings, setStandings] = useState([]);
  const [playoffSettings, setPlayoffSettings] = useState(null);
  const [leagueSport, setLeagueSport] = useState(null);

  // Setup mode: round dates/times
  const [roundDates, setRoundDates] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Score entry
  const [scores, setScores] = useState({});
  const [savingGame, setSavingGame] = useState(null);

  // Override UI
  const [overrideGame, setOverrideGame] = useState(null);
  const [overrideWinnerId, setOverrideWinnerId] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadPlayoffs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getPlayoffs(leagueId);
      setPlayoffs(response.playoffs);
      setStandings(response.standings || []);
      setPlayoffSettings(response.playoffSettings);
      setLeagueSport(response.sport || null);

      // Initialize scores from existing games
      if (response.playoffs) {
        const existingScores = {};
        response.playoffs.rounds.forEach(round => {
          round.games.forEach(game => {
            if (game.played) {
              existingScores[game.id] = {
                team1Score: game.team1Score,
                team2Score: game.team2Score
              };
            }
          });
        });
        setScores(existingScores);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    loadPlayoffs();
  }, [loadPlayoffs]);

  // Figure out how many rounds we need for setup mode
  const getNumRounds = () => {
    const teamsQualifying = playoffSettings?.teamsQualifying || standings.length;
    const numTeams = Math.min(teamsQualifying, standings.length);
    if (numTeams < 2) return 0;
    let bracketSize = 1;
    while (bracketSize < numTeams) bracketSize *= 2;
    return Math.log2(bracketSize);
  };

  const getRoundName = (round, total) => {
    const fromEnd = total - round + 1;
    if (fromEnd === 1) return 'Final';
    if (fromEnd === 2) return 'Semifinals';
    if (fromEnd === 3) return 'Quarterfinals';
    return `Round ${round}`;
  };

  const getGamesInRound = (round, total) => {
    const bracketSize = Math.pow(2, total);
    return bracketSize / Math.pow(2, round);
  };

  // Initialize round date inputs
  useEffect(() => {
    if (!playoffs && standings.length > 0) {
      const numRounds = getNumRounds();
      if (numRounds > 0 && roundDates.length === 0) {
        setRoundDates(
          Array.from({ length: numRounds }, () => ({
            date: '',
            timeSlots: ['']
          }))
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standings, playoffs]);

  const handleRoundDateChange = (roundIndex, date) => {
    setRoundDates(prev => prev.map((r, i) =>
      i === roundIndex ? { ...r, date } : r
    ));
  };

  const handleRoundTimeChange = (roundIndex, slotIndex, value) => {
    setRoundDates(prev => prev.map((r, i) => {
      if (i !== roundIndex) return r;
      const timeSlots = [...r.timeSlots];
      timeSlots[slotIndex] = value;
      return { ...r, timeSlots };
    }));
  };

  const addTimeSlot = (roundIndex) => {
    setRoundDates(prev => prev.map((r, i) =>
      i === roundIndex ? { ...r, timeSlots: [...r.timeSlots, ''] } : r
    ));
  };

  const handleGenerate = async () => {
    // Validate
    for (let i = 0; i < roundDates.length; i++) {
      if (!roundDates[i].date) {
        setError(`Please set a date for ${getRoundName(i + 1, roundDates.length)}`);
        return;
      }
      if (roundDates[i].timeSlots.some(s => !s)) {
        setError(`Please fill in all time slots for ${getRoundName(i + 1, roundDates.length)}`);
        return;
      }
    }

    try {
      setGenerating(true);
      setError('');
      await api.generatePlayoffs(leagueId, roundDates);
      showToast('Playoff bracket generated!', 'success');
      await loadPlayoffs();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleScoreChange = (gameId, field, value) => {
    setScores(prev => ({
      ...prev,
      [gameId]: {
        ...prev[gameId],
        [field]: value === '' ? '' : parseInt(value)
      }
    }));
  };

  const handleSaveScore = async (gameId) => {
    const gameScores = scores[gameId];
    if (gameScores?.team1Score == null || gameScores?.team2Score == null ||
        gameScores.team1Score === '' || gameScores.team2Score === '') {
      showToast('Enter both scores');
      return;
    }
    if (gameScores.team1Score === gameScores.team2Score) {
      showToast('Ties are not allowed in playoffs');
      return;
    }

    try {
      setSavingGame(gameId);
      await api.savePlayoffScore(leagueId, gameId, gameScores.team1Score, gameScores.team2Score);
      showToast('Score saved!', 'success');
      await loadPlayoffs();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSavingGame(null);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all playoffs? This will delete all playoff games and results.')) return;
    try {
      await api.deletePlayoffs(leagueId);
      showToast('Playoffs reset', 'success');
      setRoundDates([]);
      await loadPlayoffs();
    } catch (err) {
      showToast(err.message);
    }
  };

  const handleOpenOverride = (game) => {
    setOverrideGame(game.id);
    setOverrideWinnerId(game.team1?.id || '');
    setOverrideReason('');
  };

  const handleCancelOverride = () => {
    setOverrideGame(null);
    setOverrideWinnerId('');
    setOverrideReason('');
  };

  const handleApplyOverride = async (gameId) => {
    if (!overrideWinnerId) {
      showToast('Select a team to advance');
      return;
    }
    try {
      setSavingOverride(true);
      await api.applyPlayoffOverride(leagueId, gameId, overrideWinnerId, overrideReason);
      showToast('Override applied', 'success');
      setOverrideGame(null);
      setOverrideWinnerId('');
      setOverrideReason('');
      await loadPlayoffs();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSavingOverride(false);
    }
  };

  const handleClearOverride = async (gameId) => {
    try {
      setSavingOverride(true);
      await api.clearPlayoffOverride(leagueId, gameId);
      showToast('Override cleared', 'success');
      await loadPlayoffs();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSavingOverride(false);
    }
  };

  // Find the champion
  const getChampion = () => {
    if (!playoffs) return null;
    const finalRound = playoffs.rounds[playoffs.rounds.length - 1];
    if (!finalRound) return null;
    const finalGame = finalRound.games[0];
    if (!finalGame?.played) return null;
    if (finalGame.overrideWinner) return finalGame.overrideWinner;
    return finalGame.team1Score > finalGame.team2Score
      ? finalGame.team1
      : finalGame.team2;
  };

  if (loading) {
    return <div className="playoff-container"><div className="playoff-loading">Loading playoffs...</div></div>;
  }

  const teamsQualifying = playoffSettings?.teamsQualifying || standings.length;
  const numRounds = getNumRounds();
  const champion = getChampion();
  const sportConfig = getSportConfig(leagueSport);

  return (
    <div className="playoff-container">
      {toast && (
        <div className={`playoff-toast playoff-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="playoff-header">
        <h2>Playoffs</h2>
        <button className="playoff-back-btn" onClick={onBack}>Back</button>
      </div>

      {error && <div className="playoff-error">{error}</div>}

      {/* ========== SETUP MODE ========== */}
      {!playoffs && (
        <>
          {/* Standings with qualifying line */}
          <div className="playoff-standings-section">
            <h3>Current Standings</h3>
            {standings.length === 0 ? (
              <p>No teams found.</p>
            ) : (
              <table className="playoff-standings-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Team</th>
                    <th>W</th>
                    <th>L</th>
                    <th>T</th>
                    <th>PTS</th>
                    <th>GD</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((team, index) => {
                    const qualifying = index < teamsQualifying;
                    const isLastQualifying = index === teamsQualifying - 1 && teamsQualifying < standings.length;
                    return (
                      <tr
                        key={team.id}
                        className={`${qualifying ? 'playoff-qualifying' : ''} ${isLastQualifying ? 'playoff-qualifying-line' : ''}`}
                      >
                        <td>
                          {qualifying ? (
                            <span className="playoff-seed-badge">{index + 1}</span>
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td>{team.name}</td>
                        <td>{team.wins}</td>
                        <td>{team.losses}</td>
                        <td>{team.ties}</td>
                        <td>{team.points}</td>
                        <td>{team.goalDifferential > 0 ? '+' : ''}{team.goalDifferential}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {teamsQualifying < standings.length && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#667eea' }}>
                Top {teamsQualifying} teams qualify for playoffs
              </p>
            )}
          </div>

          {/* Round date/time configuration */}
          {numRounds > 0 && (
            <div className="playoff-round-config">
              <h3>Playoff Schedule</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Set a date and time slot(s) for each round.
              </p>
              {roundDates.map((rd, ri) => (
                <div key={ri} className="playoff-round-card">
                  <h4>{getRoundName(ri + 1, numRounds)} ({getGamesInRound(ri + 1, numRounds)} game{getGamesInRound(ri + 1, numRounds) > 1 ? 's' : ''})</h4>
                  <div className="playoff-field-row">
                    <div className="playoff-field-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={rd.date}
                        onChange={(e) => handleRoundDateChange(ri, e.target.value)}
                      />
                    </div>
                    {rd.timeSlots.map((ts, si) => (
                      <div key={si} className="playoff-field-group">
                        <label>Time {rd.timeSlots.length > 1 ? si + 1 : ''}</label>
                        <input
                          type="time"
                          value={ts}
                          onChange={(e) => handleRoundTimeChange(ri, si, e.target.value)}
                        />
                      </div>
                    ))}
                    {rd.timeSlots.length < getGamesInRound(ri + 1, numRounds) && (
                      <button
                        type="button"
                        style={{ alignSelf: 'flex-end', background: 'none', border: '1px solid #667eea', color: '#667eea', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                        onClick={() => addTimeSlot(ri)}
                      >
                        + Time
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                className="playoff-generate-btn"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate Bracket'}
              </button>
            </div>
          )}
        </>
      )}

      {/* ========== BRACKET MODE ========== */}
      {playoffs && (
        <>
          {champion && (
            <div className="playoff-champion-banner">
              <h3>Champion</h3>
              <p>{champion.name}</p>
            </div>
          )}

          <div className="playoff-actions">
            <button className="playoff-reset-btn" onClick={handleReset}>
              Reset Playoffs
            </button>
          </div>

          <div className="playoff-bracket">
            {playoffs.rounds.map((round) => (
              <div key={round.round} className="playoff-round-section">
                <div className="playoff-round-header">
                  <h3>{round.name}</h3>
                  <span className="playoff-round-date">{formatDate(round.date)}</span>
                </div>
                <div className="playoff-games-list">
                  {round.games.map((game) => {
                    const gameScores = scores[game.id] || {};
                    const team1Name = game.team1?.name || 'TBD';
                    const team2Name = game.team2?.name || 'TBD';
                    const bothTeamsKnown = game.team1 && game.team2;
                    const isSaved = game.played &&
                      game.team1Score === gameScores.team1Score &&
                      game.team2Score === gameScores.team2Score;
                    const hasOverride = !!game.overrideWinnerId;
                    const isOverrideFormOpen = overrideGame === game.id;

                    return (
                      <React.Fragment key={game.id}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.5rem 0.6rem',
                          gap: '0.5rem',
                          borderBottom: isOverrideFormOpen ? 'none' : '1px solid #f0f0f0',
                          background: hasOverride ? '#fff8f0' : (game.played ? '#f8fdf8' : 'white')
                        }}>
                          <span style={{ color: '#888', fontSize: '0.85rem', flexShrink: 0, width: '42px' }}>
                            {game.time || '--'}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
                            <span style={{
                              fontWeight: 500,
                              color: game.team1 ? '#333' : '#aaa',
                              flex: '1 1 0',
                              textAlign: 'right',
                              fontSize: '0.9rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {game.team1?.seed && <span style={{ color: '#667eea', fontSize: '0.75rem', marginRight: '0.3rem' }}>({game.team1.seed})</span>}
                              {team1Name}
                            </span>
                            {bothTeamsKnown ? (
                              sportConfig.winLossOnly ? (
                                <select
                                  value={
                                    gameScores.team1Score === 1 && gameScores.team2Score === 0 ? 'team1'
                                    : gameScores.team1Score === 0 && gameScores.team2Score === 1 ? 'team2'
                                    : ''
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === 'team1') {
                                      setScores(prev => ({ ...prev, [game.id]: { team1Score: 1, team2Score: 0 } }));
                                    } else if (val === 'team2') {
                                      setScores(prev => ({ ...prev, [game.id]: { team1Score: 0, team2Score: 1 } }));
                                    } else {
                                      setScores(prev => ({ ...prev, [game.id]: { team1Score: '', team2Score: '' } }));
                                    }
                                  }}
                                  style={{
                                    flexShrink: 0,
                                    padding: '0.25rem 0.4rem',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '4px',
                                    fontSize: '0.82rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    maxWidth: '160px'
                                  }}
                                >
                                  <option value="">Select winner...</option>
                                  <option value="team1">{team1Name} wins</option>
                                  <option value="team2">{team2Name} wins</option>
                                </select>
                              ) : (
                                <>
                                  <input
                                    type="number"
                                    min="0"
                                    style={{
                                      width: '40px',
                                      flexShrink: 0,
                                      padding: '0.25rem',
                                      textAlign: 'center',
                                      border: '2px solid #e0e0e0',
                                      borderRadius: '4px',
                                      fontSize: '0.9rem',
                                      fontWeight: 600
                                    }}
                                    value={gameScores.team1Score ?? ''}
                                    onChange={(e) => handleScoreChange(game.id, 'team1Score', e.target.value)}
                                    placeholder="-"
                                  />
                                  <span style={{ fontWeight: 600, color: '#888' }}>-</span>
                                  <input
                                    type="number"
                                    min="0"
                                    style={{
                                      width: '40px',
                                      flexShrink: 0,
                                      padding: '0.25rem',
                                      textAlign: 'center',
                                      border: '2px solid #e0e0e0',
                                      borderRadius: '4px',
                                      fontSize: '0.9rem',
                                      fontWeight: 600
                                    }}
                                    value={gameScores.team2Score ?? ''}
                                    onChange={(e) => handleScoreChange(game.id, 'team2Score', e.target.value)}
                                    placeholder="-"
                                  />
                                </>
                              )
                            ) : (
                              <span style={{ fontWeight: 600, color: '#888', padding: '0 0.5rem' }}>vs</span>
                            )}
                            <span style={{
                              fontWeight: 500,
                              color: game.team2 ? '#333' : '#aaa',
                              flex: '1 1 0',
                              textAlign: 'left',
                              fontSize: '0.9rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {team2Name}
                              {game.team2?.seed && <span style={{ color: '#667eea', fontSize: '0.75rem', marginLeft: '0.3rem' }}>({game.team2.seed})</span>}
                            </span>
                          </div>
                          {hasOverride && (
                            <span style={{
                              flexShrink: 0,
                              padding: '0.15rem 0.5rem',
                              background: '#e9a937',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              textTransform: 'uppercase'
                            }}>Override</span>
                          )}
                          {bothTeamsKnown && !hasOverride && (
                            <button
                              style={{
                                flexShrink: 0,
                                padding: '0.25rem 0.6rem',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: savingGame === game.id ? 'wait' : 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: isSaved ? '#d4edda' : '#667eea',
                                color: isSaved ? '#155724' : 'white'
                              }}
                              onClick={() => handleSaveScore(game.id)}
                              disabled={savingGame === game.id}
                            >
                              {savingGame === game.id ? '...' : (isSaved ? 'Saved' : 'Save')}
                            </button>
                          )}
                          {bothTeamsKnown && !hasOverride && !isOverrideFormOpen && (
                            <button
                              style={{
                                flexShrink: 0,
                                padding: '0.25rem 0.5rem',
                                border: '1px solid #e9a937',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: 'white',
                                color: '#e9a937'
                              }}
                              onClick={() => handleOpenOverride(game)}
                            >
                              Override
                            </button>
                          )}
                          {hasOverride && (
                            <button
                              style={{
                                flexShrink: 0,
                                padding: '0.25rem 0.5rem',
                                border: '1px solid #dc3545',
                                borderRadius: '4px',
                                cursor: savingOverride ? 'wait' : 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: 'white',
                                color: '#dc3545'
                              }}
                              onClick={() => handleClearOverride(game.id)}
                              disabled={savingOverride}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {/* Override reason display */}
                        {hasOverride && game.overrideReason && (
                          <div style={{
                            padding: '0.3rem 0.6rem 0.3rem 54px',
                            background: '#fff8f0',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.78rem',
                            color: '#996a13',
                            fontStyle: 'italic'
                          }}>
                            {game.overrideReason}
                          </div>
                        )}
                        {/* Inline override form */}
                        {isOverrideFormOpen && (
                          <div style={{
                            padding: '0.6rem 0.6rem 0.6rem 54px',
                            background: '#fffbf5',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.4rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Advance:</label>
                              <select
                                value={overrideWinnerId}
                                onChange={(e) => setOverrideWinnerId(e.target.value)}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  fontSize: '0.85rem'
                                }}
                              >
                                <option value={game.team1?.id}>{team1Name}</option>
                                <option value={game.team2?.id}>{team2Name}</option>
                              </select>
                            </div>
                            <input
                              type="text"
                              placeholder="Reason (optional, e.g. rules violation)"
                              value={overrideReason}
                              onChange={(e) => setOverrideReason(e.target.value)}
                              style={{
                                padding: '0.3rem 0.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '0.82rem',
                                maxWidth: '340px'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              <button
                                style={{
                                  padding: '0.3rem 0.75rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: savingOverride ? 'wait' : 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  background: '#e9a937',
                                  color: 'white'
                                }}
                                onClick={() => handleApplyOverride(game.id)}
                                disabled={savingOverride}
                              >
                                {savingOverride ? '...' : 'Apply Override'}
                              </button>
                              <button
                                style={{
                                  padding: '0.3rem 0.75rem',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  background: 'white',
                                  color: '#666'
                                }}
                                onClick={handleCancelOverride}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default PlayoffSetup;
